/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import { Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { ignoreMinutesIfZero } from 'components/organisms/resource-management/utils/dates';
import { BodyShort01 } from 'components/styles';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import {
  AutoAllocateIcon,
  SpinnerWrapper,
  StyledCSSSpinner,
} from './component';

const OverAllocatedStyles = css`
  background: var(--red-20);
  border-top: 2px solid var(--red-50);
`;

const UnderAllocatedStyles = css`
  background: var(--green-10);
  border-top: 2px solid var(--green-40);
`;

const Wrapper = styled.div<{
  $isOverAllocated: boolean;
  $widthPercentage: number;
  $showCursor: boolean;
  $centerContent?: boolean;
}>`
  ${BodyShort01};
  cursor: ${({ $showCursor }) => ($showCursor ? 'pointer' : 'default')};
  padding: 0 var(--spacing-05);
  display: flex;
  justify-content: ${({ $centerContent }) =>
    $centerContent ? 'center' : 'initial'};
  gap: var(--spacing-02);
  width: 100%;
  height: 100%;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${({ $widthPercentage }) => `${$widthPercentage}%`};
    max-width: 100%;
    z-index: -1;
    ${({ $isOverAllocated }) =>
      $isOverAllocated ? OverAllocatedStyles : UnderAllocatedStyles};
  }
`;
const PrimaryValueHolder = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
`;
const SecondaryValue = styled.span`
  color: var(--gray-60);
  font-size: var(--font-size-sm);
`;
const AllocationWrapper = styled.span`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const HoursSection = styled.td`
  padding-left: var(--spacing-05);
  text-align: right;
`;

const GreenText = styled.span`
  color: var(--green-40);
`;

const RedText = styled.span`
  color: var(--red-40);
`;

const TooltipContent: FC<{
  budgetedHours?: number;
  totalAllocatedInMinutes: number;
  remainingHoursInMinutes?: number;
}> = ({ budgetedHours, totalAllocatedInMinutes, remainingHoursInMinutes }) => {
  return (
    <table>
      <tr>
        <td>
          <FormattedMessage id="budgeted_hours" />:
        </td>
        <HoursSection>
          {budgetedHours != null ? (
            ignoreMinutesIfZero(budgetedHours)
          ) : (
            <span>-</span>
          )}
        </HoursSection>
      </tr>
      <tr>
        <td>
          <FormattedMessage id="resource_management.allocation.total_allocated.label" />
          :
        </td>
        <HoursSection>
          {ignoreMinutesIfZero(totalAllocatedInMinutes)}
        </HoursSection>
      </tr>
      <br />
      <tr>
        <td>
          <FormattedMessage id="resource_management.allocation.remaining_hours.label" />
          :
        </td>
        <HoursSection>
          {remainingHoursInMinutes != null ? (
            remainingHoursInMinutes > 0 ? (
              <GreenText>
                {ignoreMinutesIfZero(remainingHoursInMinutes)}
              </GreenText>
            ) : (
              <RedText>{ignoreMinutesIfZero(remainingHoursInMinutes)}</RedText>
            )
          ) : (
            <span>-</span>
          )}
        </HoursSection>
      </tr>
    </table>
  );
};

interface Props {
  budgetedHours?: number;
  totalAllocation: number;
  isTimesheetFeatureEnabled: boolean;
  showDetailedView: boolean;
  dataLoading?: boolean;
  autoAllocate: boolean;
}
export const ProjectAllocation: FC<Props> = ({
  budgetedHours,
  totalAllocation,
  isTimesheetFeatureEnabled,
  showDetailedView,
  dataLoading,
  autoAllocate,
}) => {
  const isCollaborator = useIsCurrentUserCollaborator();
  const [innerTooltipVisible, setInnerTooltipVisibililty] = useState(false);

  if (dataLoading) {
    return (
      <Wrapper
        $isOverAllocated={false}
        $widthPercentage={0}
        $showCursor={false}
        $centerContent
      >
        <SpinnerWrapper $noRightSpace indicator={<StyledCSSSpinner />} />
      </Wrapper>
    );
  }

  const hasBudgetedHoursSet =
    isTimesheetFeatureEnabled && budgetedHours != null && budgetedHours !== 0;
  const isOverAllocated = !!(
    hasBudgetedHoursSet &&
    budgetedHours != null &&
    totalAllocation > budgetedHours
  );
  const allocatedHoursPercentage =
    hasBudgetedHoursSet && budgetedHours != null
      ? (totalAllocation / budgetedHours) * 100
      : 0;

  return (
    <Wrapper
      $isOverAllocated={isOverAllocated}
      $widthPercentage={
        isTimesheetFeatureEnabled &&
        budgetedHours != null &&
        budgetedHours !== 0 &&
        !isCollaborator
          ? Math.max(4, allocatedHoursPercentage)
          : 0
      }
      $showCursor={
        isTimesheetFeatureEnabled && showDetailedView && !isCollaborator
      }
    >
      <Tooltip
        title={
          isTimesheetFeatureEnabled &&
          showDetailedView &&
          !isCollaborator &&
          !innerTooltipVisible ? (
            <TooltipContent
              budgetedHours={
                hasBudgetedHoursSet && budgetedHours != null
                  ? budgetedHours
                  : undefined
              }
              totalAllocatedInMinutes={totalAllocation}
              remainingHoursInMinutes={
                hasBudgetedHoursSet && budgetedHours != null
                  ? budgetedHours - totalAllocation
                  : undefined
              }
            />
          ) : null
        }
        placement="right"
      >
        <AllocationWrapper>
          <PrimaryValueHolder>
            {ignoreMinutesIfZero(totalAllocation)}

            {autoAllocate === true ? (
              <Tooltip
                title={
                  <FormattedMessage
                    id="resource_management.auto_allocation.tooltip"
                    values={{
                      b: (chunks) => <strong>{chunks}</strong>,
                      br: () => <br />,
                    }}
                  />
                }
                onVisibleChange={setInnerTooltipVisibililty}
              >
                <div>
                  <AutoAllocateIcon />
                </div>
              </Tooltip>
            ) : null}
          </PrimaryValueHolder>
          {isTimesheetFeatureEnabled &&
          budgetedHours != null &&
          !isCollaborator ? (
            <SecondaryValue>
              {allocatedHoursPercentage.toFixed(0)}%
            </SecondaryValue>
          ) : null}
        </AllocationWrapper>
      </Tooltip>
    </Wrapper>
  );
};
